// axios
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
// universal-cookie
import Cookies from 'universal-cookie';
//  jwt-decode
import { jwtDecode } from 'jwt-decode';

// dayjs
import dayjs from 'dayjs';

// project imports
import callAzureFunctionAuth from './call-azure-function-auth';
import { store } from '../store';
import { setLoggedOut } from '../store/features/userSlice';
import { getCookieNameSuffix } from '../utils/cookies';
import clearReduxStore from '../utils/clear-redux-store';
import { DATA_SOURCES } from '../constants/appDataSources';

interface AzureFunctionRequestConfig extends AxiosRequestConfig {
  appDataSourceId?: string;
  accessToken?: string;
}

const functionConfig: Partial<AxiosRequestConfig> = {
  baseURL: import.meta.env.VITE_APP_AZ_FUNCTIONS_BASE_URI,
  withCredentials: true
};

/**
 * Refreshes the access token whenever it is about to expire.
 * If anything goes wrong, the user is logged out.
 * @param {string} accessToken The access token
 */
const refreshAccessToken = async (accessToken: string) => {
  try {
    if (accessToken) {
      // decode token
      const decoded = jwtDecode(accessToken);

      if (decoded?.exp) {
        const expDate = dayjs(decoded.exp * 1000);
        const now = dayjs();

        // Check if it's expired OR expiring within 5 minutes
        const isExpiringSoon = expDate.diff(now, 'minute') <= 5;

        // check if token is close to expiration
        if (isExpiringSoon) {
          // refresh the token
          await callAzureFunctionAuth({
            url: `/auth-refresh-access`,
            method: 'get'
          });
        }
      }
    } else {
      throw new Error('No access token');
    }
  } catch {
    // invalidate cookies
    callAzureFunctionAuth({
      url: `/auth-remove-access`,
      method: 'get'
    });
    // log out user
    store.dispatch(setLoggedOut());
    // clear redux store values
    clearReduxStore();
    // go to login page
    window.location.assign('/login');
  }
};

const getAccessTokenFromUrl = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('accessToken');
  }
  return null;
};

/**
 * Makes a request to a protected azure function that matches the specified url.
 * @param {AxiosRequestConfig} requestConfig Axios request config. Only the `url` is required. For all available options see https://axios-http.com/docs/req_config.
 * @returns {Promise<AxiosResponse<T>>} Axios response.
 */
const callAzureFunction = async <T = unknown,>(requestConfig: AzureFunctionRequestConfig): Promise<AxiosResponse<T>> => {
  const tokenFromUrl = getAccessTokenFromUrl();

  // If we have an accessToken, add it to the query params
  let params = requestConfig.params || {};
  if (tokenFromUrl) {
    params = { ...params, accessToken: tokenFromUrl };
  } else {
    const cookies = new Cookies(null, { path: '/' });
    const cookieNameSuffix = getCookieNameSuffix();
    const accessToken = cookies.get(`AF_ATHP${cookieNameSuffix}`);
    await refreshAccessToken(accessToken);
  }

  // Destructure appDataSourceId and other params from requestConfig
  const { appDataSourceId, ...axiosConfig } = requestConfig;

  // Add appDataSourceId to params if it is provided and not 'default'
  const updatedParams = {
    ...params,
    ...(appDataSourceId && appDataSourceId !== DATA_SOURCES.default.id ? { appDataSourceId } : {})
  };

  return axios({
    ...axiosConfig,
    params: updatedParams, // use updated params with optional appDataSourceId
    ...functionConfig
  });
};

export default callAzureFunction;
